import { inject } from '@angular/core';
import { CanActivateFn, RedirectCommand, Router, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, filter, first, map, timeout } from 'rxjs/operators';
import { UserPreferencesService } from '../../../user/services/user-preferences/user-preferences.service';
import { UserPreferences } from '../../../user/entities/user-preferences/user-preferences';
import { RoutingSegment } from '../../entities/routing-segment';
import { UserRoles } from '../../../user/entities/user-roles';
import { CurafidaAuthService } from '../../../authentication/curafida-auth.service';

/**
 * Upper bound for waiting on the first authoritative preferences value.
 *
 * UserPreferencesService.init() sets `null` when the backend call fails, so a *failing* load
 * already unblocks these guards. The signal only stays `undefined` when the request never settles
 * - nothing in the HTTP layer imposes a timeout, and init() is not retried for the same user.
 * Without this bound the guard never completes, so the router navigation never finishes: the page
 * hangs on a spinner until the app is restarted, while synchronously guarded pages (the profile,
 * libs/guard/profile.guard.ts) still open normally.
 */
export const PREFERENCES_TIMEOUT_MS = 10000;

/**
 * Waits for the first authoritative preferences value and turns it into a routing decision.
 *
 * Preferences that never arrive are treated exactly like preferences that failed to load: `decide`
 * is called with `null`, which resolves to the non-kiosk branch in both guards. Getting the mode
 * wrong for one navigation is recoverable - a guard that never settles is not.
 */
const decideOnFirstPreferences = (
    userPreferencesService: UserPreferencesService,
    decide: (preferences: UserPreferences | null) => boolean | UrlTree,
): Observable<boolean | UrlTree> =>
    userPreferencesService.userPreferences$.pipe(
        filter((preferences) => preferences !== undefined),
        first(),
        timeout({ first: PREFERENCES_TIMEOUT_MS }),
        catchError(() => of(null)),
        map(decide),
    );

export const kioskGuard: CanActivateFn = () => {
    const router = inject(Router);
    const userPreferencesService = inject(UserPreferencesService);
    const authService = inject(CurafidaAuthService);

    if (!authService.loggedInUser()?.roles.includes(UserRoles.PATIENT)) {
        const urlTree: UrlTree = router.parseUrl('/member/home');
        return new RedirectCommand(urlTree, { skipLocationChange: true });
    }

    /*
     * Path: preferences have already been loaded from the backend. Read the signal synchronously
     * and act on them immediately.
     *
     * The signal always reflects the latest written value, even if the toObservable ReplaySubject
     * hasn't been updated yet by the scheduled effect. Only fall back to the observable when
     * preferences haven't loaded at all (still undefined).
     */
    const syncPrefs = userPreferencesService.userPreferences();
    if (syncPrefs !== undefined) {
        const isKioskEnabled = syncPrefs?.currentMode === 'KIOSK';
        return isKioskEnabled
            ? true
            : router.createUrlTree([RoutingSegment.MEMBER, RoutingSegment.PATIENT_MANAGEMENT, RoutingSegment.HOME]);
    }

    /*
     * Path: preferences haven't loaded yet (signal is still undefined: its initial state before
     * the backend responds). This happens on the very first navigation after login, while init()
     * is still in-flight. Wait for the first non-undefined emission before acting.
     *
     * Filtering on undefined ensures we always act on the authoritative backend value,
     * never on a stale intermediate state.
     */
    return decideOnFirstPreferences(userPreferencesService, (prefs) => {
        const isKioskEnabled = prefs?.currentMode === 'KIOSK';
        return isKioskEnabled
            ? true
            : router.createUrlTree([RoutingSegment.MEMBER, RoutingSegment.PATIENT_MANAGEMENT, RoutingSegment.HOME]);
    });
};

export const homePatientPageGuard: CanActivateFn = () => {
    const router = inject(Router);
    const userPreferencesService = inject(UserPreferencesService);
    const authService = inject(CurafidaAuthService);

    if (!authService.loggedInUser()?.roles.includes(UserRoles.PATIENT)) {
        const urlTree: UrlTree = router.parseUrl('/member/home');
        return new RedirectCommand(urlTree, { skipLocationChange: true });
    }

    /*
     * Path: preferences have already been loaded from the backend. Read the signal synchronously
     * and act on them immediately.
     *
     * The signal always reflects the latest written value, even if the toObservable ReplaySubject
     * hasn't been updated yet by the scheduled effect. Only fall back to the observable when
     * preferences haven't loaded at all (still undefined).
     */
    const syncPrefs = userPreferencesService.userPreferences();
    if (syncPrefs !== undefined) {
        const isKioskEnabled = syncPrefs?.currentMode === 'KIOSK';
        return isKioskEnabled ? router.createUrlTree([RoutingSegment.MEMBER, RoutingSegment.KIOSK]) : true;
    }

    /*
     * Path: preferences haven't loaded yet (signal is still undefined: its initial state before
     * the backend responds). This happens on the very first navigation after login, while init()
     * is still in-flight. Wait for the first non-undefined emission before acting.
     *
     * Filtering on undefined ensures we always act on the authoritative backend value,
     * never on a stale intermediate state.
     */
    return decideOnFirstPreferences(userPreferencesService, (prefs) => {
        const isKioskEnabled = prefs?.currentMode === 'KIOSK';
        return isKioskEnabled ? router.createUrlTree([RoutingSegment.MEMBER, RoutingSegment.KIOSK]) : true;
    });
};
